import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { captureReferral } from "@/lib/referral";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import DemoTry from "@/components/DemoTry";
import {
  Sparkles, Leaf, Calendar, ShoppingBag, Flame, Heart, Utensils, ArrowRight, Scale, Star, ShieldCheck,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1546069901-d5bfd2cbfb1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxoZWFsdGh5JTIwZm9vZCUyMGJvd2wlMjBzYWxhZCUyMGdyYWluJTIwYm93bCUyMGRpc2h8ZW58MHx8fHwxNzkwNjUxMjQzfDA&ixlib=rb-4.1.0&q=85";
const VEG = "https://images.unsplash.com/photo-1590779033100-9f60a05a013d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMG9yZ2FuaWMlMjBjb29raW5nJTIwaW5ncmVkaWVudHMlMjB2ZWdldGFibGVzJTIwaGVyYnN8ZW58MHx8fHwxNzkwNjUxMjM2fDA&ixlib=rb-4.1.0&q=85";
const GRAIN = "https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwZm9vZCUyMGJvd2wlMjBzYWxhZCUyMGdyYWluJTIwYm93bCUyMGRpc2h8ZW58MHx8fHwxNzkwNjUxMjQzfDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Sparkles, title: "Pantry-to-Plate AI", desc: "Type what's in your kitchen. Claude builds recipes around it and flags exactly what you're missing.", tone: "#C85A32" },
  { icon: Heart, title: "Health Coach Mode", desc: "Set macro & calorie goals. Every recipe is tuned to move you toward them.", tone: "#4A6B4E" },
  { icon: Leaf, title: "Zero-Waste Kitchen", desc: "Track freshness, remix leftovers, and watch the money & waste you save add up.", tone: "#8FA885" },
  { icon: Calendar, title: "Weekly Planner", desc: "Assign meals across the week, then compile everything into one smart list.", tone: "#D49B4B" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [journalPosts, setJournalPosts] = useState([]);
  const [journalLoading, setJournalLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setJournalPosts(r.data)).catch(() => {}).finally(() => setJournalLoading(false));
  }, []);

  useEffect(() => {
    captureReferral();
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/studio", { replace: true });
  }, [user, loading, navigate]);

  const login = () => {
    navigate("/login");
  };
  const register = () => {
    navigate("/login?mode=register");
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#2C221E] grain overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#FBF9F5]/80 backdrop-blur-md border-b border-[#E2D9CC]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-[#C85A32] flex items-center justify-center">
              <Utensils className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <span className="font-serif text-2xl tracking-tight">NourishCraft<span className="text-[#C85A32]">.app</span></span>
          </div>
          <Button
            onClick={login}
            data-testid="nav-login-btn"
            className="rounded-full bg-[#2C221E] hover:bg-[#3d2f28] text-white px-6 h-11 transition-colors"
          >
            Sign in
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-8 items-center">
          <div className="md:col-span-6 animate-fade-up">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">
              AI Culinary Studio · Zero-Waste Kitchen
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5">
              Cook what you have.
              <br />
              <span className="italic text-[#C85A32]">Waste nothing.</span>
              <br />
              Nourish everything.
            </h1>
            <p className="text-base md:text-lg text-[#6E6259] leading-relaxed mt-6 max-w-lg">
              NourishCraft turns your pantry into personalized, healthy recipes — complete with nutrition,
              meal plans, and auto shopping lists that skip what you already own.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button
                onClick={register}
                data-testid="hero-cta-btn"
                className="group rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white h-13 px-8 py-6 text-base transition-colors"
              >
                Start cooking free
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-[#6E6259]">
                <ShieldCheck className="w-4 h-4 text-[#4A6B4E]" />
                Free for 30 days
              </div>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <span className="text-sm text-[#6E6259]">Free for 30 days.</span>
            </div>
            <div className="flex items-center gap-6 mt-8 pt-8 border-t border-[#E2D9CC]">
              {[["Recipes, on demand", "AI"], ["Macro tracking", "Live"]].map(([l, v]) => (
                <div key={l}>
                  <div className="font-serif text-2xl text-[#2C221E]">{v}</div>
                  <div className="text-xs text-[#9C8F85] mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[#2C221E]/10 border border-[#E2D9CC]">
              <img src={HERO} alt="Healthy grain bowl" className="w-full h-[420px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C221E]/40 to-transparent" />
            </div>
            {/* Floating macro card */}
            <div className="absolute -left-4 md:-left-8 top-16 bg-white rounded-2xl shadow-xl border border-[#E2D9CC] p-4 w-44 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              <div className="flex items-center gap-2 text-[#4A6B4E]">
                <Flame className="w-4 h-4" />
                <span className="text-xs font-mono uppercase tracking-wider">Per serving</span>
              </div>
              <div className="font-serif text-3xl mt-1">438 kcal</div>
              <div className="flex gap-3 mt-2 text-xs text-[#6E6259]">
                <span>P 32g</span><span>C 41g</span><span>F 14g</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Try it demo */}
      <DemoTry />

      {/* From the Journal */}
      {!journalLoading && journalPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="landing-journal-section">
          <div className="max-w-xl">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">The NourishCraft Journal</span>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
              From the Journal
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {journalPosts.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`landing-journal-card-${p.slug}`}
                className="group bg-white rounded-2xl border border-[#E2D9CC] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-44 w-full object-cover" />}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-[#9C8F85]"><Calendar className="w-3.5 h-3.5" />{new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}{p.source === "ai" && <span className="ml-1 px-2 py-0.5 rounded-full bg-[#FDF5E6] text-[#8a6d2a]">AI</span>}</div>
                  <h2 className="font-serif text-2xl mt-2 leading-snug group-hover:text-[#C85A32] transition-colors">{p.title}</h2>
                  <p className="text-sm text-[#6E6259] mt-2 line-clamp-3 flex-1">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#C85A32] font-medium mt-4">Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/blog" className="inline-flex items-center rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white px-8 h-12 transition-colors">
              Read the journal <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>
      )}

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="max-w-xl">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">The flagship experience</span>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
            Four kitchens, one studio
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="md:col-span-6 lg:col-span-3 bg-white rounded-2xl border border-[#E2D9CC] p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${f.tone}1a` }}>
                <f.icon className="w-5 h-5" style={{ color: f.tone }} strokeWidth={2} />
              </div>
              <h3 className="font-serif text-xl">{f.title}</h3>
              <p className="text-sm text-[#6E6259] leading-relaxed mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Split showcase */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-[2rem] overflow-hidden h-80 group">
            <img src={VEG} alt="Fresh produce" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C221E]/70 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <ShoppingBag className="w-6 h-6 text-white mb-2" />
              <h3 className="font-serif text-2xl text-white">Smart shopping lists</h3>
              <p className="text-sm text-white/80 mt-1 max-w-sm">Compiled from your meal plan, grouped by aisle, minus what's already in your pantry.</p>
            </div>
          </div>
          <div className="relative rounded-[2rem] overflow-hidden h-80 group">
            <img src={GRAIN} alt="Grain bowl" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C221E]/70 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <Scale className="w-6 h-6 text-white mb-2" />
              <h3 className="font-serif text-2xl text-white">Nutrition you can trust</h3>
              <p className="text-sm text-white/80 mt-1 max-w-sm">Calories and macros on every recipe, tuned to the goals you set in Health Coach.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="pricing-section">
        <div className="max-w-xl">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">Simple pricing</span>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3">
            Your first month is on us
          </h2>
          <p className="text-[#6E6259] mt-3">Cook unlimited recipes free for 30 days. After that, less than a coffee a month — cancel anytime.</p>
          <div className="inline-flex items-center gap-2 mt-4 text-sm text-[#4A6B4E] bg-[#E8F0E6] rounded-full px-4 py-1.5" data-testid="no-card-badge">
            <ShieldCheck className="w-4 h-4" /> Free for 30 days
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-3xl">
          <div className="bg-white rounded-2xl border border-[#E2D9CC] p-8">
            <div className="text-xs font-mono uppercase tracking-wide text-[#9C8F85]">Monthly</div>
            <div className="font-serif text-4xl mt-2">$3.99<span className="text-base text-[#9C8F85]">/mo</span></div>
            <p className="text-sm text-[#6E6259] mt-2">Unlimited recipes, dish photos, planner & more.</p>
            <Button onClick={register} data-testid="pricing-monthly-btn" className="w-full mt-6 rounded-full bg-[#2C221E] hover:bg-[#3d2f28] text-white h-11">Start free month</Button>
          </div>
          <div className="relative bg-[#2C221E] rounded-2xl p-8 text-white">
            <span className="absolute -top-3 right-6 bg-[#4A6B4E] text-white text-[10px] font-mono uppercase px-3 py-1 rounded-full">Best value · 2 months free</span>
            <div className="text-xs font-mono uppercase tracking-wide text-[#D49B4B]">Annual</div>
            <div className="font-serif text-4xl mt-2">$39.99<span className="text-base text-[#C5B8A5]">/yr</span></div>
            <p className="text-sm text-[#C5B8A5] mt-2">Just $3.33/mo billed yearly. Everything in Premium.</p>
            <Button onClick={register} data-testid="pricing-yearly-btn" className="w-full mt-6 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white h-11">Start free month</Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="bg-[#2C221E] rounded-[2rem] px-8 md:px-16 py-16 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-serif text-3xl md:text-5xl text-white tracking-tight">
              Your pantry is a recipe waiting to happen.
            </h2>
            <p className="text-[#C5B8A5] mt-4 max-w-md mx-auto">Create your free account and craft your first healthy meal in under a minute.</p>
            <Button
              onClick={register}
              data-testid="footer-cta-btn"
              className="rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white h-13 px-8 py-6 text-base mt-8 transition-colors"
            >
              Get started free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 md:px-10 py-10 border-t border-[#E2D9CC] text-sm text-[#9C8F85] flex items-center justify-between">
        <span className="font-serif text-lg text-[#2C221E]">NourishCraft<span className="text-[#C85A32]">.app</span></span>
        <div className="flex items-center gap-5 flex-wrap">
          <a href="/blog" data-testid="landing-blog-link" className="hover:text-[#C85A32]">Blog</a>
          <a href="/faq" data-testid="landing-faq-link" className="hover:text-[#C85A32]">FAQ</a>
          <a href="/terms" className="hover:text-[#C85A32]">User Agreement</a>
          <a href="/privacy" data-testid="landing-privacy-link" className="hover:text-[#C85A32]">Privacy Policy</a>
          <span className="hidden sm:inline">Crafted with fresh ingredients · {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
